package ai.wagemix.net.models

import com.fsryan.foundation.models.FSStringEnumerated
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Instant

/**
 * The state of a job-description upload (`users/{uid}/uploads/{id}.status`). Rebuild plan
 * task API-6 adds the field; an upload document without one is [Ready] when a stored mix
 * references it and [Processing] otherwise.
 */
enum class WMUploadStatus(override val stringValue: String): FSStringEnumerated {
    Processing("processing"),
    Ready("ready"),
    Failed("failed"),
    /** Rejected by the virus scan or the file checks. */
    Rejected("rejected"),
    Unknown("unknown")
}

/**
 * Why an upload produced no mix (`users/{uid}/uploads/{id}.failureReason`); see
 * `planning/job-description-intake-plan.md` §3. None of them counts against the upload limit.
 */
enum class WMUploadFailureReason(override val stringValue: String): FSStringEnumerated {
    /** The AI found no job description in the file or page; the file was deleted. */
    NotJobDescription("not_job_description"),
    /** The posting URL could not be rendered to a PDF. Retryable. */
    PageUnavailable("page_unavailable"),
    /** Malware, or content aimed at manipulating the analysis; the file was deleted or never stored. */
    Malicious("malicious"),
    /** The analysis itself failed. Retryable. */
    AnalysisFailed("analysis_failed"),
    Unknown("unknown");

    /** Whether running the pipeline again can help: the file (or page) is still there and was not refused. */
    val retryable: Boolean get() = this == PageUnavailable || this == AnalysisFailed || this == Unknown
}

/** How the job description reached the API. */
enum class WMUploadSource(override val stringValue: String): FSStringEnumerated {
    File("file"),
    Url("url"),
    Unknown("unknown")
}

/** A job-description upload as the Job Descriptions page lists it. */
interface WMUpload {
    val id: String
    val originalFileName: String
    val contentType: String
    val size: Long
    val uploadedAt: Instant?
    val status: WMUploadStatus
    val statusMessage: String?
    val source: WMUploadSource
    /** The posting URL when [source] is [WMUploadSource.Url]. */
    val sourceUrl: String?
    /** The stored mix this upload produced, when known. */
    val mixId: String?
    /** When the pipeline last wrote [status]; `null` on documents from before it recorded the time. */
    val statusUpdatedAt: Instant?
    /** Why a failed or rejected upload produced no mix; `null` otherwise, and on older documents. */
    val failureReason: WMUploadFailureReason?

    companion object
}

/**
 * How long an upload may stay processing before it no longer counts as running: the re-evaluation workflow's own
 * time limit, so a run that died without writing its outcome does not lock its mix for good.
 */
val WM_UPLOAD_PROCESSING_STALE_AFTER: Duration = 1.hours

/**
 * Whether this upload uses one of the plan's job-description uploads (`planning/job-description-intake-plan.md` §4):
 * it produced a mix (whatever happened since, such as a failed re-evaluation), or it is being analysed and was marked
 * so within [WM_UPLOAD_PROCESSING_STALE_AFTER] of [now]. Failed and rejected uploads are free.
 */
fun WMUpload.countsAgainstUploadLimit(now: Instant): Boolean {
    if (mixId != null || status == WMUploadStatus.Ready) {
        return true
    }
    val since = statusUpdatedAt ?: return false
    return status == WMUploadStatus.Processing && now - since < WM_UPLOAD_PROCESSING_STALE_AFTER
}

/**
 * `true` while [mixId] is being re-evaluated: the upload it came from is processing again, and was marked so within
 * [WM_UPLOAD_PROCESSING_STALE_AFTER] of [now] (or at an unrecorded time).
 */
fun List<WMUpload>.isReevaluating(mixId: String, now: Instant): Boolean {
    return any { upload ->
        val since = upload.statusUpdatedAt
        upload.mixId == mixId &&
            upload.status == WMUploadStatus.Processing &&
            (since == null || now - since < WM_UPLOAD_PROCESSING_STALE_AFTER)
    }
}

fun WMUpload.Companion.of(
    id: String,
    originalFileName: String,
    contentType: String,
    size: Long,
    uploadedAt: Instant?,
    status: WMUploadStatus,
    statusMessage: String?,
    source: WMUploadSource,
    sourceUrl: String?,
    mixId: String?,
    statusUpdatedAt: Instant?,
    failureReason: WMUploadFailureReason?
): WMUpload {
    return WMUploadData(
        id, originalFileName, contentType, size, uploadedAt, status, statusMessage, source, sourceUrl, mixId,
        statusUpdatedAt, failureReason
    )
}

private data class WMUploadData(
    override val id: String,
    override val originalFileName: String,
    override val contentType: String,
    override val size: Long,
    override val uploadedAt: Instant?,
    override val status: WMUploadStatus,
    override val statusMessage: String?,
    override val source: WMUploadSource,
    override val sourceUrl: String?,
    override val mixId: String?,
    override val statusUpdatedAt: Instant?,
    override val failureReason: WMUploadFailureReason?
): WMUpload
